import type { OnboardingVendors } from '#modules/analytics';

import { configureVidaPostHog } from './posthog';
import { vidaAnalytics } from './vidaAnalytics';
import { setVidaVendorConfig, type VidaVendorConfig } from './vidaVendorConfig';
import { VidaVendorProvider } from './VidaVendorProvider';

export type { VidaVendorConfig };
export { resetVidaVendorConfig } from './vidaVendorConfig';

/**
 * Vida's own vendor adapter: PostHog + Datadog, built from credentials you pass.
 *
 * **This module is where the vendor SDKs live, and the only place.** The core
 * (`@vidahealth/onboarding`) reaches none of them; importing this module is what pulls in
 * `posthog-js` / `posthog-react-native` and the four `@datadog/*` packages. Metro does not
 * tree-shake, so a separate import site is the sole way a consumer avoids evaluating them —
 * a boolean could never have achieved that.
 *
 * **Not published.** It is absent from both `exports` and `files` in `package.json`, so the
 * six SDKs are `devDependencies` — not peers — and never reach a consumer's install. The
 * standalone app in `app/_layout.tsx` is the only caller. Publishing it would take two
 * lines: `@/types/user` → `#types/user` in `analytics/utils/analytics.ts` and
 * `analytics/types/analyticsPort.ts`, which are the only `@/` specifiers left in this
 * module's reachable graph. Nobody needs it published today — a host passes its own
 * `analytics`, which is the whole premise — so it stays internal by choice, not by defect.
 *
 * Who should call this:
 *
 * - **The standalone app** — yes. It is the root; nobody else owns these vendors.
 * - **A host app (login, VidaReactNative)** — no. It already runs PostHog against the same
 *   project and already owns the Datadog singletons, so it passes its own `analytics`
 *   instead and never imports this file. See `docs/vendor-ownership-proposal.md` §1 for why
 *   sharing the host's client is not merely cheaper but *more correct* than building a
 *   second one.
 *
 * ```tsx
 * const vendors = createVidaVendors({ vidaEnv, posthog: {…}, datadog: {…} });
 * <OnboardingRoot {...vendors} apiClient={apiClient} config={ONBOARDING_CONFIG}>
 * ```
 *
 * Call it **once, at module scope**, and spread the result — not inline in a render. The
 * returned object is a new identity each call, and `Provider` landing on `OnboardingRoot`
 * as a fresh component type would remount the entire subtree on every render. Module scope
 * is also where a `process.env` read still inlines, so the config literal naturally lives
 * beside it.
 *
 * PostHog is constructed here, synchronously, rather than from the provider's effect: the
 * client must exist before the RN `PostHogProvider` mounts with it, and before any child
 * can capture. Datadog stays in its provider's effect, which is late enough — it reads
 * config at mount, after this function has published it.
 */
export const createVidaVendors = (config: VidaVendorConfig): OnboardingVendors => {
  // Publish first: `configureVidaPostHog` reads it immediately, and `VidaVendorProvider`'s
  // Datadog effect reads it at mount.
  setVidaVendorConfig(config);
  configureVidaPostHog();

  return {
    analytics: vidaAnalytics,
    Provider: VidaVendorProvider,
    // Standalone there is no other tracker, so we own onboarding pageviews.
    trackScreens: true,
  };
};
